import { Component, effect, inject, input, OnDestroy, OnInit, signal } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, of, Subscription } from 'rxjs';
import { isEqual } from 'lodash';
import { ZiroFormAbstractDirective } from '../../../../forms/ziro-form-abstract.directive';
import { ZiroFormsModule } from '../../../../forms/ziro-forms.module';
import { ZiroFormConfig, ZiroFormsUpdate, ZiroFormsValidationState } from '../../../../forms/ziro-forms-models';
import { ZiroRadioConfig } from '../../../../forms/fields/radio/ziro-radio.component';
import { ZiroTextareaConfig } from '../../../../forms/fields/textarea/ziro-textarea.component';
import { ZiroFileUploadConfig } from '../../../../forms/fields/file-upload/ziro-file-upload.component';
import { HtmlInputType, ZiroTextConfig } from '../../../../forms/fields/text/ziro-text.component';
import { ZiroSelectConfig, ZiroSelectOption } from '../../../../forms/fields/select/ziro-select.component';
import {
  HtmlCheckboxType,
  HtmlSwitchSize,
  ZiroCheckboxConfig,
} from '../../../../forms/fields/checkbox/ziro-checkbox.component';
import {
  CallQueueExceptionHandling,
  CallQueueExceptionHandlingAction,
  CallQueueExceptionHandlingTargetType,
  CallQueueExceptionHandlingView,
  NoAgentApplyTo,
  NoAgentSettingsView,
  OverflowSettingsView,
  TimeoutSettingsView,
  VoiceAppViewEntityType,
} from '../../../../shared/models/generated/ziroModels';
import { TeamsAutoAttendantsDestinationsSearchService } from '../../../../teams-auto-attendants/teams-auto-attendants-destinations-search.service';
import { E164_REGEX } from '../../../../teams-auto-attendants/teams-auto-attendant-edit-form/call-flows/menu-button-form/menu-button-form.component';
import { ZiroIcons } from '../../../../shared/models/ziro-icons.enum';
import { ButtonStyles } from '../../../../button/button.component';

export enum ExceptionHandlingSectionType {
  OVERFLOW = 'OVERFLOW',
  TIMEOUT = 'TIMEOUT',
  NO_AGENTS = 'NO_AGENTS',
}

export enum ExceptionHandlingPromptType {
  NONE = 'NONE',
  TTS_PROMPT = 'TTS_PROMPT',
  AUDIO_FILE = 'AUDIO_FILE',
}

export enum ExceptionHandlingRedirectType {
  USER = 'USER',
  VOICE_APP = 'VOICE_APP',
  RESOURCE_ACCOUNT = 'RESOURCE_ACCOUNT',
  EXTERNAL_PHONE = 'EXTERNAL_PHONE',
  PERSONAL_MAILBOX = 'PERSONAL_MAILBOX',
  SHARED_MAILBOX = 'SHARED_MAILBOX',
}

export interface UiExceptionHandlingSettings {
  overflowThreshold: number | string | null;
  timeoutMinutes: number | string | null;
  timeoutSeconds: number;
  noAgentApplyToNewCallsOnly: boolean;
  action: CallQueueExceptionHandlingAction;
  promptType: ExceptionHandlingPromptType;
  ttsPrompt: string;
  audioPromptUpload: File | null;
  audioPromptFileGuid: string;
  redirectType: ExceptionHandlingRedirectType | null;
  redirectTarget: ZiroSelectOption | null;
  externalPhoneNumber: string;
  enableTranscription: boolean;
  suppressSystemMessage: boolean;
}

export function defaultUiExceptionHandlingSettings(
  sectionType: ExceptionHandlingSectionType,
): UiExceptionHandlingSettings {
  return {
    overflowThreshold: null,
    timeoutMinutes: null,
    timeoutSeconds: 0,
    noAgentApplyToNewCallsOnly: false,
    action:
      sectionType === ExceptionHandlingSectionType.NO_AGENTS
        ? CallQueueExceptionHandlingAction.QUEUE
        : CallQueueExceptionHandlingAction.DISCONNECT,
    promptType: ExceptionHandlingPromptType.NONE,
    ttsPrompt: '',
    audioPromptUpload: null,
    audioPromptFileGuid: '',
    redirectType: null,
    redirectTarget: null,
    externalPhoneNumber: '',
    enableTranscription: true,
    suppressSystemMessage: false,
  };
}

export function mapOverflowSettingsViewToUi(
  overflowSettings: OverflowSettingsView | null | undefined,
): UiExceptionHandlingSettings {
  const uiSettings = mapExceptionHandlingViewToUi(
    ExceptionHandlingSectionType.OVERFLOW,
    overflowSettings?.exceptionHandling,
  );
  uiSettings.overflowThreshold = overflowSettings?.overflowThreshold ?? 200;
  return uiSettings;
}

export function mapTimeoutSettingsViewToUi(
  timeoutSettings: TimeoutSettingsView | null | undefined,
): UiExceptionHandlingSettings {
  const uiSettings = mapExceptionHandlingViewToUi(
    ExceptionHandlingSectionType.TIMEOUT,
    timeoutSettings?.exceptionHandling,
  );
  const timeoutThresholdInSeconds = timeoutSettings?.timeoutThresholdInSeconds ?? 1200;
  uiSettings.timeoutMinutes = Math.floor(timeoutThresholdInSeconds / 60);
  uiSettings.timeoutSeconds = timeoutThresholdInSeconds % 60;
  return uiSettings;
}

export function mapNoAgentSettingsViewToUi(
  noAgentSettings: NoAgentSettingsView | null | undefined,
): UiExceptionHandlingSettings {
  const uiSettings = mapExceptionHandlingViewToUi(
    ExceptionHandlingSectionType.NO_AGENTS,
    noAgentSettings?.exceptionHandling,
  );
  uiSettings.noAgentApplyToNewCallsOnly = noAgentSettings?.noAgentApplyTo === NoAgentApplyTo.NEW_CALLS;
  return uiSettings;
}

function mapExceptionHandlingViewToUi(
  sectionType: ExceptionHandlingSectionType,
  view: CallQueueExceptionHandlingView | null | undefined,
): UiExceptionHandlingSettings {
  const uiSettings = defaultUiExceptionHandlingSettings(sectionType);

  if (!view || view.action === CallQueueExceptionHandlingAction.UNKNOWN) {
    return uiSettings;
  }

  uiSettings.action = view.action;
  uiSettings.audioPromptFileGuid = view.audioPromptFileGuid ?? '';
  uiSettings.ttsPrompt = view.ttsPrompt ?? '';
  uiSettings.promptType = view.audioPromptFileGuid
    ? ExceptionHandlingPromptType.AUDIO_FILE
    : view.ttsPrompt
      ? ExceptionHandlingPromptType.TTS_PROMPT
      : ExceptionHandlingPromptType.NONE;

  if (view.action === CallQueueExceptionHandlingAction.REDIRECT && view.target) {
    switch (view.targetType) {
      case CallQueueExceptionHandlingTargetType.USER:
        uiSettings.redirectType = ExceptionHandlingRedirectType.USER;
        break;
      case CallQueueExceptionHandlingTargetType.PERSONAL_MAILBOX:
        uiSettings.redirectType = ExceptionHandlingRedirectType.PERSONAL_MAILBOX;
        break;
      case CallQueueExceptionHandlingTargetType.RESOURCE_ACCOUNT:
        uiSettings.redirectType = ExceptionHandlingRedirectType.RESOURCE_ACCOUNT;
        break;
      case CallQueueExceptionHandlingTargetType.CALL_QUEUE:
      case CallQueueExceptionHandlingTargetType.AUTO_ATTENDANT:
        uiSettings.redirectType = ExceptionHandlingRedirectType.VOICE_APP;
        break;
      case CallQueueExceptionHandlingTargetType.SHARED_MAILBOX:
        uiSettings.redirectType = ExceptionHandlingRedirectType.SHARED_MAILBOX;
        break;
      case CallQueueExceptionHandlingTargetType.EXTERNAL_PHONE:
        uiSettings.redirectType = ExceptionHandlingRedirectType.EXTERNAL_PHONE;
        uiSettings.externalPhoneNumber = view.target.lineUri?.split('tel:').pop() ?? '';
        break;
    }

    if (uiSettings.redirectType && uiSettings.redirectType !== ExceptionHandlingRedirectType.EXTERNAL_PHONE) {
      uiSettings.redirectTarget = {
        label: view.target.displayName || view.target.upn,
        value: view.target,
        secondaryContent: view.target.upn ?? '',
      };
    }

    if (view.targetType === CallQueueExceptionHandlingTargetType.SHARED_MAILBOX && view.sharedVoicemailSettings) {
      uiSettings.enableTranscription = view.sharedVoicemailSettings.enableSharedVoicemailTranscription;
      uiSettings.suppressSystemMessage = view.sharedVoicemailSettings.enableSharedVoicemailSystemPromptSuppression;
    }
  }

  return uiSettings;
}

export function mapUiToExceptionHandling(
  uiSettings: UiExceptionHandlingSettings,
  audioPromptFileGuid: string,
): CallQueueExceptionHandling {
  const isRedirect = uiSettings.action === CallQueueExceptionHandlingAction.REDIRECT;
  const isSharedMailbox = isRedirect && uiSettings.redirectType === ExceptionHandlingRedirectType.SHARED_MAILBOX;
  const isPromptApplicable = isExceptionHandlingPromptApplicable(uiSettings);

  let targetType = CallQueueExceptionHandlingTargetType.NONE;
  let target = '';

  if (isRedirect) {
    switch (uiSettings.redirectType) {
      case ExceptionHandlingRedirectType.USER:
        targetType = CallQueueExceptionHandlingTargetType.USER;
        break;
      case ExceptionHandlingRedirectType.PERSONAL_MAILBOX:
        targetType = CallQueueExceptionHandlingTargetType.PERSONAL_MAILBOX;
        break;
      case ExceptionHandlingRedirectType.RESOURCE_ACCOUNT:
        targetType = CallQueueExceptionHandlingTargetType.RESOURCE_ACCOUNT;
        break;
      case ExceptionHandlingRedirectType.VOICE_APP:
        targetType =
          uiSettings.redirectTarget?.value?.type === VoiceAppViewEntityType.AUTO_ATTENDANT
            ? CallQueueExceptionHandlingTargetType.AUTO_ATTENDANT
            : CallQueueExceptionHandlingTargetType.CALL_QUEUE;
        break;
      case ExceptionHandlingRedirectType.SHARED_MAILBOX:
        targetType = CallQueueExceptionHandlingTargetType.SHARED_MAILBOX;
        break;
      case ExceptionHandlingRedirectType.EXTERNAL_PHONE:
        targetType = CallQueueExceptionHandlingTargetType.EXTERNAL_PHONE;
        break;
    }

    target =
      uiSettings.redirectType === ExceptionHandlingRedirectType.EXTERNAL_PHONE
        ? (uiSettings.externalPhoneNumber ?? '')
        : (uiSettings.redirectTarget?.value?.guid ?? uiSettings.redirectTarget?.value?.id ?? '');
  }

  return {
    action: uiSettings.action,
    target,
    targetType,
    ttsPrompt:
      isPromptApplicable && uiSettings.promptType === ExceptionHandlingPromptType.TTS_PROMPT
        ? uiSettings.ttsPrompt
        : '',
    audioPromptFileGuid:
      isPromptApplicable && uiSettings.promptType === ExceptionHandlingPromptType.AUDIO_FILE ? audioPromptFileGuid : '',
    sharedVoicemailSettings: isSharedMailbox
      ? {
          enableSharedVoicemailTranscription: uiSettings.enableTranscription,
          enableSharedVoicemailSystemPromptSuppression: uiSettings.suppressSystemMessage,
        }
      : null,
  };
}

export function isExceptionHandlingPromptApplicable(uiSettings: UiExceptionHandlingSettings): boolean {
  if (uiSettings.action === CallQueueExceptionHandlingAction.DISCONNECT) {
    return true;
  }
  if (uiSettings.action === CallQueueExceptionHandlingAction.REDIRECT) {
    return (
      uiSettings.redirectType === ExceptionHandlingRedirectType.VOICE_APP ||
      uiSettings.redirectType === ExceptionHandlingRedirectType.RESOURCE_ACCOUNT ||
      uiSettings.redirectType === ExceptionHandlingRedirectType.EXTERNAL_PHONE ||
      uiSettings.redirectType === ExceptionHandlingRedirectType.SHARED_MAILBOX
    );
  }
  return false;
}

@Component({
  selector: 'ziro-call-queue-exception-handling-form',
  imports: [ZiroFormsModule, TranslateModule],
  templateUrl: './call-queue-exception-handling-form.component.html',
  providers: [TeamsAutoAttendantsDestinationsSearchService],
})
export class CallQueueExceptionHandlingFormComponent
  extends ZiroFormAbstractDirective<UiExceptionHandlingSettings>
  implements OnInit, OnDestroy
{
  sectionType = input.required<ExceptionHandlingSectionType>();
  tenantId = input.required<string>();
  dataAutomation = input.required<string>();
  agentAlertTime = input.required<number>();

  private _translateService = inject(TranslateService);
  private _searchService = inject(TeamsAutoAttendantsDestinationsSearchService);

  audioPromptUploadURL = signal('');
  audioPromptDownloadName = signal('');
  audioPromptFileExtension = signal('');

  acceptedFileTypes = ['.wav', '.wma', '.mp3'];
  formConfig: ZiroFormConfig;

  private _suppressNextUpdate = false;
  private _subscriptions = new Subscription();

  constructor() {
    super();

    effect(() => {
      this.agentAlertTime();
      this.fieldChannels?.['timeoutMinutes']?.validateSource.next();
    });
  }

  ngOnInit(): void {
    if (this.entity?.audioPromptUpload) {
      this._setAudioPromptPreview(this.entity.audioPromptUpload);
    }

    const updateSub = this.ziroFormsUpdate$.subscribe((update: ZiroFormsUpdate<UiExceptionHandlingSettings>) =>
      this._handleFormUpdate(update),
    );
    this._subscriptions.add(updateSub);

    this.formConfig = {
      fields: {
        ...this._buildSectionSpecificFields(),
        action: {
          dataAutomation: `${this.dataAutomation()}-action`,
          label: `tkey;teams_call_queue.exception_handling.${this.sectionType().toLowerCase()}.action.label`,
          componentConfig: new ZiroRadioConfig<CallQueueExceptionHandlingAction>({
            stackLabelAndOptions: true,
            buttons: [
              ...(this.sectionType() === ExceptionHandlingSectionType.NO_AGENTS
                ? [
                    {
                      value: CallQueueExceptionHandlingAction.QUEUE,
                      label: 'tkey;teams_call_queue.exception_handling.action.queue',
                    },
                  ]
                : []),
              {
                value: CallQueueExceptionHandlingAction.DISCONNECT,
                label: 'tkey;teams_call_queue.exception_handling.action.disconnect',
              },
              {
                value: CallQueueExceptionHandlingAction.REDIRECT,
                label: 'tkey;teams_call_queue.exception_handling.action.redirect',
              },
            ],
          }),
        },
        redirectType: {
          dataAutomation: `${this.dataAutomation()}-redirect-type`,
          label: 'tkey;teams_call_queue.exception_handling.redirect_type.label',
          componentConfig: new ZiroSelectConfig({
            options: [
              {
                value: ExceptionHandlingRedirectType.USER,
                label: this._translateService.instant('tkey;teams_call_queue.exception_handling.redirect_type.user'),
              },
              {
                value: ExceptionHandlingRedirectType.VOICE_APP,
                label: this._translateService.instant(
                  'tkey;teams_call_queue.exception_handling.redirect_type.voice_app',
                ),
              },
              {
                value: ExceptionHandlingRedirectType.RESOURCE_ACCOUNT,
                label: this._translateService.instant(
                  'tkey;teams_call_queue.exception_handling.redirect_type.resource_account',
                ),
              },
              {
                value: ExceptionHandlingRedirectType.EXTERNAL_PHONE,
                label: this._translateService.instant(
                  'tkey;teams_call_queue.exception_handling.redirect_type.external_phone',
                ),
              },
              {
                value: ExceptionHandlingRedirectType.PERSONAL_MAILBOX,
                label: this._translateService.instant(
                  'tkey;teams_call_queue.exception_handling.redirect_type.personal_voicemail',
                ),
              },
              {
                value: ExceptionHandlingRedirectType.SHARED_MAILBOX,
                label: this._translateService.instant(
                  'tkey;teams_call_queue.exception_handling.redirect_type.shared_voicemail',
                ),
              },
            ],
            bindValue: 'value',
            hideClear: true,
            placeholder: 'tkey;teams_call_queue.exception_handling.redirect_type.placeholder',
          }),
          required: () => this._isRedirect,
          hidden: () => !this._isRedirect,
          valExcluded: () => !this._isRedirect,
        },
        redirectTarget: {
          dataAutomation: `${this.dataAutomation()}-redirect-target`,
          label: 'tkey;teams_call_queue.exception_handling.redirect_target.label',
          componentConfig: this._buildRedirectTargetConfig(),
          required: () => this._isRedirect && !!this.formData?.redirectType && !this._isExternalPhoneRedirect,
          hidden: () => !this._isRedirect || !this.formData?.redirectType || this._isExternalPhoneRedirect,
          valExcluded: () => !this._isRedirect || !this.formData?.redirectType || this._isExternalPhoneRedirect,
        },
        externalPhoneNumber: {
          dataAutomation: `${this.dataAutomation()}-external-phone-number`,
          label: 'tkey;teams_call_queue.exception_handling.redirect_target.label',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
          required: () => this._isRedirect && this._isExternalPhoneRedirect,
          hidden: () => !this._isRedirect || !this._isExternalPhoneRedirect,
          valExcluded: () => !this._isRedirect || !this._isExternalPhoneRedirect,
          validation: [
            {
              validator: (val: string) =>
                !val || E164_REGEX.test(val) ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
              message: 'tkey;validators.global.error.invalid_number.pattern',
            },
          ],
        },
        enableTranscription: {
          dataAutomation: `${this.dataAutomation()}-transcription`,
          label: 'tkey;teams_call_queue.exception_handling.shared_voicemail.transcription.label',
          componentConfig: new ZiroCheckboxConfig({
            checkboxType: HtmlCheckboxType.SWITCH,
            size: HtmlSwitchSize.LG,
            checkboxClass: 'ms-auto',
          }),
          hidden: () => !this._isSharedMailboxRedirect,
          valExcluded: () => !this._isSharedMailboxRedirect,
        },
        suppressSystemMessage: {
          dataAutomation: `${this.dataAutomation()}-suppress-system-message`,
          label: 'tkey;teams_call_queue.exception_handling.shared_voicemail.suppress_system_message.label',
          componentConfig: new ZiroCheckboxConfig({
            checkboxType: HtmlCheckboxType.SWITCH,
            size: HtmlSwitchSize.LG,
            checkboxClass: 'ms-auto',
          }),
          hidden: () => !this._isSharedMailboxRedirect,
          valExcluded: () => !this._isSharedMailboxRedirect,
        },
        promptType: {
          dataAutomation: `${this.dataAutomation()}-prompt-type`,
          label: 'tkey;teams_call_queue.exception_handling.prompt.label',
          componentConfig: this._buildPromptTypeConfig(
            this.entity?.action === CallQueueExceptionHandlingAction.REDIRECT &&
              this.entity?.redirectType === ExceptionHandlingRedirectType.SHARED_MAILBOX,
          ),
          hidden: () => !this.isPromptApplicable,
          valExcluded: () => !this.isPromptApplicable,
        },
        ttsPrompt: {
          dataAutomation: `${this.dataAutomation()}-tts-prompt`,
          label: '',
          componentConfig: new ZiroTextareaConfig({
            placeholder: this._translateService.instant(
              'tkey;teams_call_queue.audio_settings.greeting.text_to_speech.placeholder',
            ),
          }),
          required: () => this.isPromptApplicable && this._isTtsPrompt,
          hidden: () => !this.isPromptApplicable || !this._isTtsPrompt,
          valExcluded: () => !this.isPromptApplicable || !this._isTtsPrompt,
          validation: [
            {
              validator: (val: string) =>
                val && val.length >= 10 && val.length <= 1000
                  ? ZiroFormsValidationState.VALID
                  : ZiroFormsValidationState.INVALID,
              message: () =>
                this._translateService.instant('tkey;validators.global.min_max.char.error', {
                  min: 10,
                  max: 1000,
                }),
            },
          ],
        },
        audioPromptUpload: {
          dataAutomation: `${this.dataAutomation()}-audio-prompt-upload`,
          label: '',
          componentConfig: new ZiroFileUploadConfig({
            acceptedFileExtensions: { acceptedExtensions: this.acceptedFileTypes.join(', '), allowOthers: false },
            isAudioFile: true,
          }),
          required: () => this.isPromptApplicable && this._isAudioPrompt,
          valExcluded: () => !this.isPromptApplicable || !this._isAudioPrompt,
          validation: [
            {
              validator: (val: File) =>
                !!val && val.name.length > 64 ? ZiroFormsValidationState.INVALID : ZiroFormsValidationState.VALID,
              message: this._translateService.instant('tkey;global.validation.file_name.max_length', {
                maxLength: 64,
              }),
            },
            {
              validator: (val: File): ZiroFormsValidationState =>
                !val || val.size <= 5000000 ? ZiroFormsValidationState.VALID : ZiroFormsValidationState.INVALID,
              message: () => ({
                content: 'tkey;shared.fileselect.validators.max_size',
                params: {
                  size: '5 MB',
                },
              }),
            },
          ],
        },
      },
      options: {
        columnClasses: {
          label: 'text-start',
          input: '',
        },
      },
    };
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
    this._revokeAudioPromptPreviewUrl();
  }

  validateAndShowErrors(): boolean {
    this._setIsFormSubmitted(true);
    this.validateAllFields();
    return this.isFormValid();
  }

  protected submit(): Observable<null> {
    return of(null);
  }

  private get _isRedirect(): boolean {
    return this.formData?.action === CallQueueExceptionHandlingAction.REDIRECT;
  }

  private get _isExternalPhoneRedirect(): boolean {
    return this.formData?.redirectType === ExceptionHandlingRedirectType.EXTERNAL_PHONE;
  }

  private get _isSharedMailboxRedirect(): boolean {
    return this._isRedirect && this.formData?.redirectType === ExceptionHandlingRedirectType.SHARED_MAILBOX;
  }

  protected get isPromptApplicable(): boolean {
    return isExceptionHandlingPromptApplicable(this.formData ?? this.entity);
  }

  private get _isTtsPrompt(): boolean {
    return this.formData?.promptType === ExceptionHandlingPromptType.TTS_PROMPT;
  }

  private get _isAudioPrompt(): boolean {
    return this.formData?.promptType === ExceptionHandlingPromptType.AUDIO_FILE;
  }

  private _buildSectionSpecificFields(): ZiroFormConfig['fields'] {
    switch (this.sectionType()) {
      case ExceptionHandlingSectionType.OVERFLOW:
        return {
          overflowThreshold: {
            dataAutomation: `${this.dataAutomation()}-threshold`,
            label: 'tkey;teams_call_queue.exception_handling.overflow.threshold.label',
            componentConfig: new ZiroTextConfig({
              htmlInputType: HtmlInputType.NUMBER,
            }),
            labelToolTipIconClass: ZiroIcons.TOOLTIP,
            labelToolTipText: 'tkey;teams_call_queue.exception_handling.overflow.threshold.tooltip',
            required: () => true,
            validation: [
              {
                validator: (val) =>
                  Number(val) < 0 || Number(val) > 200 || !Number.isInteger(Number(val))
                    ? ZiroFormsValidationState.INVALID
                    : ZiroFormsValidationState.VALID,
                message: this._translateService.instant('tkey;validators.global.min_max.error', {
                  min: 0,
                  max: 200,
                }),
              },
            ],
          },
        };
      case ExceptionHandlingSectionType.TIMEOUT:
        return {
          timeoutMinutes: {
            dataAutomation: `${this.dataAutomation()}-minutes`,
            label: 'tkey;teams_call_queue.exception_handling.timeout.minutes.label',
            componentConfig: new ZiroTextConfig({
              htmlInputType: HtmlInputType.NUMBER,
            }),
            required: () => true,
            validation: [
              {
                validator: (val, timeoutSeconds: number) => {
                  const minutes = Number(val);
                  if (minutes < 0 || minutes > 45 || !Number.isInteger(minutes)) {
                    return ZiroFormsValidationState.INVALID;
                  }
                  return minutes * 60 + Number(timeoutSeconds ?? 0) > 2700
                    ? ZiroFormsValidationState.INVALID
                    : ZiroFormsValidationState.VALID;
                },
                injectValuesFromFields: ['timeoutSeconds'],
                message: 'tkey;teams_call_queue.exception_handling.timeout.minutes.validation',
              },
              {
                validator: (val, timeoutSeconds: number) =>
                  Number(val) * 60 + Number(timeoutSeconds) < this.agentAlertTime()
                    ? ZiroFormsValidationState.INVALID
                    : ZiroFormsValidationState.VALID,
                injectValuesFromFields: ['timeoutSeconds'],
                message: 'tkey;teams_call_queue.exception_handling.timeout.agent_alert_time.validation',
              },
            ],
          },
          timeoutSeconds: {
            dataAutomation: `${this.dataAutomation()}-seconds`,
            label: 'tkey;teams_call_queue.exception_handling.timeout.seconds.label',
            componentConfig: this._buildTimeoutSecondsConfig(Number(this.entity?.timeoutMinutes) >= 45),
            required: () => true,
          },
        };
      case ExceptionHandlingSectionType.NO_AGENTS:
        return {
          noAgentApplyToNewCallsOnly: {
            dataAutomation: `${this.dataAutomation()}-apply-to-new-calls-only`,
            label: 'tkey;teams_call_queue.exception_handling.no_agents.new_calls_only.label',
            componentConfig: new ZiroCheckboxConfig({
              checkboxType: HtmlCheckboxType.SWITCH,
              size: HtmlSwitchSize.LG,
            }),
            labelToolTipIconClass: ZiroIcons.TOOLTIP,
            labelToolTipText: 'tkey;teams_call_queue.exception_handling.no_agents.new_calls_only.tooltip',
          },
        };
    }
  }

  private _buildTimeoutSecondsConfig(minutesAtMax: boolean): ZiroSelectConfig {
    const secondsOptions = minutesAtMax ? [0] : [0, 15, 30, 45];
    return new ZiroSelectConfig({
      options: secondsOptions.map((seconds) => ({ label: `${seconds}`, value: seconds })),
      bindValue: 'value',
      hideClear: true,
    });
  }

  private _buildPromptTypeConfig(isSharedMailboxRedirect: boolean): ZiroRadioConfig<ExceptionHandlingPromptType> {
    return new ZiroRadioConfig<ExceptionHandlingPromptType>({
      stackLabelAndOptions: true,
      buttons: [
        ...(isSharedMailboxRedirect
          ? []
          : [
              {
                value: ExceptionHandlingPromptType.NONE,
                label: 'tkey;teams_call_queue.exception_handling.prompt.none',
              },
            ]),
        {
          value: ExceptionHandlingPromptType.TTS_PROMPT,
          label: 'tkey;teams_call_queue.exception_handling.prompt.text_to_speech',
        },
        {
          value: ExceptionHandlingPromptType.AUDIO_FILE,
          label: 'tkey;teams_call_queue.exception_handling.prompt.audio_file',
        },
      ],
    });
  }

  private _buildRedirectTargetConfig(): ZiroSelectConfig {
    return new ZiroSelectConfig({
      isMultiSelect: false,
      keepSearchInput: true,
      minSearchLength: 3,
      asyncOptionsFn: this._fetchRedirectTarget.bind(this),
      placeholder: 'tkey;teams_call_queue.exception_handling.redirect_target.placeholder',
    });
  }

  private _fetchRedirectTarget(query: string): Observable<ZiroSelectOption[]> {
    const redirectType = this.formData?.redirectType;
    if (!query || query.length < 3 || !redirectType) {
      return of([]);
    }

    switch (redirectType) {
      case ExceptionHandlingRedirectType.USER:
      case ExceptionHandlingRedirectType.PERSONAL_MAILBOX:
        return this._searchService.searchUsers(this.tenantId(), query);
      case ExceptionHandlingRedirectType.VOICE_APP:
        return this._searchService.searchVoiceApps(this.tenantId(), query);
      case ExceptionHandlingRedirectType.RESOURCE_ACCOUNT:
        return this._searchService.searchResourceAccounts(this.tenantId(), query);
      case ExceptionHandlingRedirectType.SHARED_MAILBOX:
        return this._searchService.searchVoicemailGroups(this.tenantId(), query);
      default:
        return of([]);
    }
  }

  private _handleFormUpdate(update: ZiroFormsUpdate<UiExceptionHandlingSettings>): void {
    if (this._suppressNextUpdate) {
      this._suppressNextUpdate = false;
      return;
    }
    if (!update.new || !update.old || isEqual(update.new, update.old)) {
      return;
    }

    this._updateAudioPromptPreview(update);

    if (update.new.redirectType !== update.old.redirectType) {
      this._onRedirectTypeChange(update.new);
      return;
    }

    if (
      this.sectionType() === ExceptionHandlingSectionType.TIMEOUT &&
      (update.new.timeoutMinutes !== update.old.timeoutMinutes ||
        update.new.timeoutSeconds !== update.old.timeoutSeconds)
    ) {
      this._onTimeoutSettingsChange(update.new);
    }
  }

  private _onRedirectTypeChange(newSettings: UiExceptionHandlingSettings): void {
    const isSharedMailbox = newSettings.redirectType === ExceptionHandlingRedirectType.SHARED_MAILBOX;
    const promptType =
      isSharedMailbox && newSettings.promptType === ExceptionHandlingPromptType.NONE
        ? ExceptionHandlingPromptType.TTS_PROMPT
        : newSettings.promptType;

    const keepsPrompt = isExceptionHandlingPromptApplicable(newSettings);

    this._suppressNextUpdate = true;
    this.entitySource.next({
      ...newSettings,
      promptType,
      audioPromptUpload: keepsPrompt ? newSettings.audioPromptUpload : null,
      audioPromptFileGuid: keepsPrompt ? newSettings.audioPromptFileGuid : '',
      redirectTarget: null,
      externalPhoneNumber: '',
    });
    if (!keepsPrompt) {
      this._clearAudioPromptPreview();
    }

    this.fieldChannels?.['redirectTarget']?.componentConfigSource.next(this._buildRedirectTargetConfig());
    this.fieldChannels?.['promptType']?.componentConfigSource.next(this._buildPromptTypeConfig(isSharedMailbox));
  }

  private _onTimeoutSettingsChange(newSettings: UiExceptionHandlingSettings): void {
    const minutesAtMax = Number(newSettings.timeoutMinutes) >= 45;

    if (minutesAtMax && Number(newSettings.timeoutSeconds) > 0) {
      this.fieldChannels?.['timeoutSeconds']?.entitySource.next(0);
      return;
    }

    this.fieldChannels?.['timeoutSeconds']?.componentConfigSource.next(this._buildTimeoutSecondsConfig(minutesAtMax));
  }

  private _updateAudioPromptPreview(update: ZiroFormsUpdate<UiExceptionHandlingSettings>): void {
    const newUpload = update.new.audioPromptUpload;
    const oldUpload = update.old.audioPromptUpload;

    if (newUpload && newUpload !== oldUpload) {
      this._setAudioPromptPreview(newUpload);
    } else if (!newUpload && oldUpload) {
      this._clearAudioPromptPreview();
    }
  }

  private _clearAudioPromptPreview(): void {
    this._revokeAudioPromptPreviewUrl();
    this.audioPromptUploadURL.set('');
    this.audioPromptDownloadName.set('');
    this.audioPromptFileExtension.set('');
  }

  private _setAudioPromptPreview(file: File): void {
    this._revokeAudioPromptPreviewUrl();
    const lastDot = file.name.lastIndexOf('.');
    this.audioPromptFileExtension.set(file.name.substring(lastDot));
    this.audioPromptUploadURL.set(URL.createObjectURL(file));
    this.audioPromptDownloadName.set(file.name);
  }

  private _revokeAudioPromptPreviewUrl(): void {
    if (this.audioPromptUploadURL()) {
      URL.revokeObjectURL(this.audioPromptUploadURL());
    }
  }

  protected readonly ExceptionHandlingSectionType = ExceptionHandlingSectionType;
  protected readonly ExceptionHandlingPromptType = ExceptionHandlingPromptType;
  protected readonly CallQueueExceptionHandlingAction = CallQueueExceptionHandlingAction;
  protected readonly ziroIcons = ZiroIcons;
  protected readonly buttonStyles = ButtonStyles;
}
